import { useRef, useState, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  Input,
  useColorModeValue,
  IconButton,
} from '@chakra-ui/react';
import { Avatar, type PictureOwner } from '../Avatar';
import { Camera, X } from 'lucide-react';
import { useT } from '../../i18n';
import { avatarDataUrl } from '../../lib/photos';

// The original can be a full camera photo; it's shrunk to 512 px before
// sending (issue #24).
const MAX_SIZE = 25 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// `ownerId` and `familyId` open the current picture (D294).
interface AvatarUploadProps extends PictureOwner {
  value?: string | null;
  onChange: (base64: string | null) => void;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  name?: string;
}

export function AvatarUpload({
  value,
  onChange,
  size = 'xl',
  name,
  ownerId,
  familyId,
}: AvatarUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const t = useT();

  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const hoverBorderColor = useColorModeValue('brand.500', 'brand.300');
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const errorColor = useColorModeValue('red.500', 'red.300');

  const processFile = useCallback(
    (file: File) => {
      setError(null);

      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(t('account.avatar.wrongType'));
        return;
      }

      if (file.size > MAX_SIZE) {
        setError(t('account.avatar.tooBig'));
        return;
      }

      avatarDataUrl(file)
        .then(onChange)
        .catch(() => setError(t('account.avatar.readFailed')));
    },
    [onChange, t]
  );

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const sizeMap = {
    sm: { avatar: 'sm', container: '60px' },
    md: { avatar: 'md', container: '80px' },
    lg: { avatar: 'lg', container: '100px' },
    xl: { avatar: 'xl', container: '120px' },
    '2xl': { avatar: '2xl', container: '150px' },
  };

  return (
    <VStack spacing={2}>
      <Box
        position="relative"
        cursor="pointer"
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Box
          w={sizeMap[size].container}
          h={sizeMap[size].container}
          borderRadius="full"
          border="2px dashed"
          borderColor={isDragging ? hoverBorderColor : borderColor}
          bg={bgColor}
          display="flex"
          alignItems="center"
          justifyContent="center"
          transition="all 0.2s"
          _hover={{ borderColor: hoverBorderColor }}
        >
          {value ? (
            <Avatar
              size={sizeMap[size].avatar}
              src={value}
              name={name}
              ownerId={ownerId}
              familyId={familyId}
            />
          ) : (
            <VStack spacing={1}>
              <Camera size={24} />
              <Text fontSize="xs" color="gray.500">
                {t('account.avatar.upload')}
              </Text>
            </VStack>
          )}
        </Box>

        {value && (
          <IconButton
            aria-label={t('account.avatar.remove')}
            icon={<X size={14} />}
            size="xs"
            colorScheme="red"
            borderRadius="full"
            position="absolute"
            top={0}
            right={0}
            onClick={handleRemove}
          />
        )}

        <Input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          onChange={handleChange}
          display="none"
        />
      </Box>

      {error && (
        <Text fontSize="sm" color={errorColor}>
          {error}
        </Text>
      )}
    </VStack>
  );
}
